import { useCallback, useEffect, useRef, useState } from "react";
import type { ActionSummary, BuilderApi, PublishRequest, Workspace } from "../api/contract";
import { toBuilderError, type BuilderFailure } from "../api/errors";
import { inFlightPublication, isInFlight, isReviewStale, isTerminal, newActionId } from "../lib/workspaceState";

/**
 * Publication is prepare -> review -> explicit confirm of the exact proposal digest.
 * Every state here reflects a server answer; nothing advances on a timer.
 */
export type PublicationState =
  | { phase: "idle" }
  | { phase: "preparing"; request: PublishRequest }
  /** The prepare request may or may not have reached the server. Retrying resends the same action ID. */
  | { phase: "prepare-unknown"; request: PublishRequest; failure: BuilderFailure }
  | { phase: "reviewing"; action: ActionSummary }
  | { phase: "confirming"; action: ActionSummary }
  /** The confirmation may or may not have been applied. Retrying resends the same digest for the same action. */
  | { phase: "confirm-unknown"; action: ActionSummary; failure: BuilderFailure }
  | {
      phase: "publishing";
      action: ActionSummary;
      connection: "connected" | "reconnecting";
      /** Generation that owns the polling loop. A new value restarts polling. */
      follow: number;
      stopRefused?: boolean;
    }
  | { phase: "settled"; action: ActionSummary }
  | {
      phase: "blocked";
      reason: "stale" | "changed" | "refused" | "signed-out" | "not-found";
      action?: ActionSummary;
      /** Which request the server refused, for `refused`. */
      stage?: "prepare" | "confirm";
    };

export interface UsePublicationOptions {
  api: BuilderApi;
  siteId: string;
  workspace: Workspace | undefined;
  /** Ask for fresh server state. Resolves with the new workspace when it could be read. */
  refreshWorkspace: () => Promise<Workspace | undefined>;
  /** Poll delays in ms; the last value repeats. */
  pollDelays?: number[];
}

const DEFAULT_POLL_DELAYS = [800, 1200, 2000, 3000, 5000];
const MAX_POLL_DELAY = 10_000;

export function usePublication({ api, siteId, workspace, refreshWorkspace, pollDelays = DEFAULT_POLL_DELAYS }: UsePublicationOptions) {
  const [state, setState] = useState<PublicationState>({ phase: "idle" });
  // Each user intent gets a generation; late answers for an abandoned intent are ignored.
  const generation = useRef(0);
  const stateRef = useRef(state);
  stateRef.current = state;

  const begin = () => ++generation.current;
  const current = (token: number) => token === generation.current;

  const hardFailure = useCallback((failure: BuilderFailure, action?: ActionSummary): PublicationState | null => {
    if (failure === "signed-out") return { phase: "blocked", reason: "signed-out", action };
    if (failure === "not-found") return { phase: "blocked", reason: "not-found", action };
    return null;
  }, []);

  /** Show whatever the server said about a publication. Settled outcomes also refresh the workspace. */
  const apply = useCallback((action: ActionSummary, token: number, extra?: { stopRefused?: boolean }) => {
    if (action.state === "awaiting-confirmation") return setState({ phase: "reviewing", action });
    if (isInFlight(action)) return setState({ phase: "publishing", action, connection: "connected", follow: token, ...extra });
    setState({ phase: "settled", action });
    void refreshWorkspace();
  }, [refreshWorkspace]);

  // A different site is a different flow.
  useEffect(() => {
    begin();
    setState({ phase: "idle" });
  }, [siteId]);

  const prepare = useCallback(async (request: PublishRequest) => {
    const token = begin();
    setState({ phase: "preparing", request });
    try {
      const action = await api.preparePublication(siteId, request);
      if (current(token)) apply(action, token);
    } catch (caught) {
      if (!current(token)) return;
      const error = toBuilderError(caught);
      const hard = hardFailure(error.failure);
      if (hard) return setState(hard);
      if (error.outcomeUnknown) return setState({ phase: "prepare-unknown", request, failure: error.failure });
      // Refused. Only say the website changed if the refreshed state shows it did.
      const fresh = await refreshWorkspace();
      if (!current(token)) return;
      const moved = fresh !== undefined
        && (fresh.site.draftId !== request.baseDraftId || fresh.site.servingEpoch !== request.expectedServingEpoch);
      setState(moved ? { phase: "blocked", reason: "changed" } : { phase: "blocked", reason: "refused", stage: "prepare" });
    }
  }, [api, siteId, apply, hardFailure, refreshWorkspace]);

  /** Prepare a review of the current draft. A new review always gets a new action ID. */
  const start = useCallback(() => {
    if (!workspace) return;
    void prepare({
      actionId: newActionId(),
      kind: "publish",
      baseDraftId: workspace.site.draftId,
      expectedServingEpoch: workspace.site.servingEpoch,
    });
  }, [prepare, workspace]);

  /** Re-open a prepared review found on the server (after reload). Never confirms it. */
  const resume = useCallback((action: ActionSummary) => {
    apply(action, begin());
  }, [apply]);

  const sendConfirmation = useCallback(async (action: ActionSummary) => {
    if (!action.proposalDigest) return;
    const token = begin();
    setState({ phase: "confirming", action });
    try {
      const confirmed = await api.confirmPublication(siteId, action.id, action.proposalDigest);
      if (current(token)) apply(confirmed, token);
    } catch (caught) {
      if (!current(token)) return;
      const error = toBuilderError(caught);
      const hard = hardFailure(error.failure, action);
      if (hard) return setState(hard);
      if (error.outcomeUnknown) return setState({ phase: "confirm-unknown", action, failure: error.failure });
      // Refused. Find out whether it was confirmed elsewhere, cancelled, or the review went stale.
      const [latest, fresh] = await Promise.all([
        api.getAction(siteId, action.id).catch(() => null),
        refreshWorkspace(),
      ]);
      if (!current(token)) return;
      if (latest && latest.state !== "awaiting-confirmation") return apply(latest, token);
      const review = latest?.review ?? action.review;
      if (fresh && review && isReviewStale(review, fresh.site)) return setState({ phase: "blocked", reason: "stale", action });
      setState({ phase: "blocked", reason: "refused", action, stage: "confirm" });
    }
  }, [api, siteId, apply, hardFailure, refreshWorkspace]);

  /** Explicit owner confirmation of the review currently on screen. */
  const confirm = useCallback(() => {
    const now = stateRef.current;
    if (now.phase !== "reviewing" || !workspace) return;
    if (now.action.review && isReviewStale(now.action.review, workspace.site)) {
      begin();
      setState({ phase: "blocked", reason: "stale", action: now.action });
      return;
    }
    void sendConfirmation(now.action);
  }, [sendConfirmation, workspace]);

  /** Resend exactly the request whose outcome is unknown. */
  const retry = useCallback(() => {
    const now = stateRef.current;
    if (now.phase === "prepare-unknown") void prepare(now.request);
    if (now.phase === "confirm-unknown") void sendConfirmation(now.action);
  }, [prepare, sendConfirmation]);

  /** Cancel a review that is still waiting, or a publication that has not started building. */
  const cancel = useCallback(async () => {
    const now = stateRef.current;
    const action = now.phase === "reviewing" || now.phase === "publishing" || now.phase === "blocked" ? now.action : undefined;
    if (!action) return;
    const token = begin();
    try {
      const { action: latest, cancelled } = await api.cancelAction(siteId, action.id);
      if (!current(token)) return;
      if (cancelled) {
        setState({ phase: "idle" });
        void refreshWorkspace();
        return;
      }
      apply(latest, token, { stopRefused: true });
    } catch (caught) {
      if (!current(token)) return;
      const error = toBuilderError(caught);
      const hard = hardFailure(error.failure, action);
      if (hard) return setState(hard);
      // Could not reach the server; as far as we know the action is unchanged. Re-read it.
      const latest = await api.getAction(siteId, action.id).catch(() => null);
      if (!current(token)) return;
      if (latest) return apply(latest, token);
      // Still unreachable: keep following the same action under this generation.
      if (now.phase === "publishing") setState({ ...now, connection: "reconnecting", follow: token });
      else setState(now);
    }
  }, [api, siteId, apply, hardFailure, refreshWorkspace]);

  /** Review the current draft again, letting go of a review that can no longer be confirmed. */
  const reviewAgain = useCallback(() => {
    const now = stateRef.current;
    const old = "action" in now && now.action?.state === "awaiting-confirmation" ? now.action : undefined;
    // Best effort: an abandoned review is harmless, the server would refuse to confirm it anyway.
    if (old) void api.cancelAction(siteId, old.id).catch(() => undefined);
    void (async () => {
      const fresh = await refreshWorkspace();
      const base = fresh ?? workspace;
      if (!base) return;
      void prepare({ actionId: newActionId(), kind: "publish", baseDraftId: base.site.draftId, expectedServingEpoch: base.site.servingEpoch });
    })();
  }, [api, siteId, prepare, refreshWorkspace, workspace]);

  /** Close the flow without touching server state. A waiting review stays available to resume. */
  const dismiss = useCallback(() => {
    begin();
    setState({ phase: "idle" });
  }, []);

  // Follow queued/running publications through the action endpoint, which also repairs runtime handoff.
  const followingId = state.phase === "publishing" ? state.action.id : null;
  const followToken = state.phase === "publishing" ? state.follow : null;
  useEffect(() => {
    if (!followingId || followToken === null) return;
    const controller = new AbortController();
    let attempt = 0;
    let failures = 0;
    let timer: ReturnType<typeof setTimeout>;
    const live = () => !controller.signal.aborted && current(followToken);
    const schedule = () => {
      const delay = failures > 0
        ? Math.min(MAX_POLL_DELAY, 1000 * 2 ** failures)
        : pollDelays[Math.min(attempt, pollDelays.length - 1)]!;
      attempt += 1;
      timer = setTimeout(poll, delay);
    };
    const poll = async () => {
      try {
        const action = await api.getAction(siteId, followingId, controller.signal);
        if (!live()) return;
        failures = 0;
        if (isTerminal(action)) return apply(action, followToken);
        setState((prev) => prev.phase === "publishing" ? { ...prev, action, connection: "connected" } : prev);
        schedule();
      } catch (caught) {
        if (!live()) return;
        const error = toBuilderError(caught);
        const hard = hardFailure(error.failure);
        if (hard) return setState(hard);
        failures += 1;
        setState((prev) => prev.phase === "publishing" ? { ...prev, connection: "reconnecting" } : prev);
        schedule();
      }
    };
    schedule();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [api, siteId, followingId, followToken, pollDelays, apply, hardFailure]);

  // After reload or reconnect, keep following a publication that is already building.
  const adoptable = workspace ? inFlightPublication(workspace) : null;
  useEffect(() => {
    if (adoptable && stateRef.current.phase === "idle") {
      setState({ phase: "publishing", action: adoptable, connection: "connected", follow: begin() });
    }
  }, [adoptable]);

  return { state, start, resume, confirm, retry, cancel, reviewAgain, dismiss };
}

export type PublicationController = ReturnType<typeof usePublication>;
